import Steps from "common/Steps/Steps";
import React, { useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import { StepOne } from "features/settings/Security/TwoFA/StepOne";
import { StepTwo } from "features/settings/Security/TwoFA/StepTwo";
import { StepThree } from "features/settings/Security/TwoFA/StepThree";
import { StepConfig, StepStatus } from "features/settings/Security/TwoFA/types";
import { useHistory, useRouteMatch } from "react-router-dom";
import {
  CONFIGURATION_DISABLE_TWO_FA_PATH,
  SECURITY_SETTINGS_PATH,
  COLLAPSED_DESKTOP_MENU,
  MOBILE_SEARCH,
} from "common/constants";
import useMedia from "utils/hooks/useMedia";
import Title from "common/Typography/Title";
import Card from "common/Boxes/Card";
import { openMessage } from "utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import NewLookFormPanel from "features/settings/NewLook/NewLookFormPanel.tsx";

const TwoFAWizard = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const isDisableTwoFAMode = useRouteMatch(CONFIGURATION_DISABLE_TWO_FA_PATH);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { isNewLook } = useTheme();
  const history = useHistory();
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const getStepsConfig: () => StepConfig[] = () =>
    isDisableTwoFAMode
      ? [
          {
            component: StepThree,
            status: "process",
            title: t("two_fa.disable_title"),
          },
        ]
      : [
          {
            component: StepOne,
            status: "process",
            title: t("two_fa.download_app_title"),
          },
          {
            component: StepTwo,
            status: "wait",
            title: t("two_fa.scan_qr_title"),
          },
          {
            component: StepThree,
            status: "wait",
            title: t("two_fa.activate_title"),
          },
        ];

  const [steps, setSteps] = useState<StepConfig[]>(getStepsConfig());

  const updateStep = (
    nextStepIndex: number,
    status: StepStatus,
    currentSteps: StepConfig[] = steps
  ) => {
    const updatedSteps = currentSteps.map((step, index) => {
      return index === nextStepIndex ? { ...step, status: status } : step;
    });
    setSteps(updatedSteps);
    return updatedSteps;
  };

  const onUpdateStepStatus = (
    status: StepStatus,
    index: number,
    message?: string
  ) => {
    const updatedSteps = updateStep(index, status);
    const nextStepIndex = index + 1;

    switch (status) {
      case "finish":
        if (nextStepIndex < MAX_STEP) {
          updateStep(nextStepIndex, "process", updatedSteps);
          setCurrentStepIndex(nextStepIndex);
        }
        break;
      case "error":
        if (message) openMessage(message, "error");
        setSteps([...steps]);
        break;
    }
  };
  const MAX_STEP = steps.length;

  const CurrentStep = useMemo(() => {
    const { component: CurrentStep } = steps[currentStepIndex];
    return (
      <CurrentStep
        index={currentStepIndex}
        onUpdateStepStatus={onUpdateStepStatus}
      />
    );
  }, [currentStepIndex]);

  const goBackToStep = (stepIndex: number) => {
    if (currentStepIndex <= stepIndex) return;
    const updatedSteps = steps.map((step, index) => {
      if (index >= stepIndex && index <= currentStepIndex) {
        return {
          ...step,
          status:
            index === stepIndex
              ? ("process" as StepStatus)
              : ("wait" as StepStatus),
        };
      }
      return step;
    });
    setSteps(updatedSteps);
    setCurrentStepIndex(stepIndex);
  };

  const items = steps.map((step, index) => ({
    style: { cursor: "pointer" },
    key: step.title,
    title: step.title,
    status: step.status,
    disabled: currentStepIndex < index,
  }));

  const getStepsHeader = () => {
    if (isMobile)
      return (
        <Title level={3} style={{ textAlign: "center" }}>
          {steps[currentStepIndex].title}
        </Title>
      );

    if (!isDisableTwoFAMode) {
      return (
        <Steps
          size="small"
          current={currentStepIndex}
          style={{ userSelect: "none" }}
          items={items}
          onChange={goBackToStep}
        />
      );
    }
  };

  if (isNewLook) {
    return (
      <NewLookFormPanel
        wide={true}
        title={
          isDisableTwoFAMode
            ? t("two_fa.disable_title")
            : t("security.google_authenticator")
        }
        onBack={() => history.replace(SECURITY_SETTINGS_PATH)}
        backLabel={tCommon("page_title.account_and_security")}
      >
        {!isDisableTwoFAMode &&
          (isMobile ? (
            <NewLookStepCaption>
              {`${currentStepIndex + 1}/${steps.length} · ${
                steps[currentStepIndex].title
              }`}
            </NewLookStepCaption>
          ) : (
            <Steps
              size="small"
              current={currentStepIndex}
              style={{ userSelect: "none" }}
              items={items}
              onChange={goBackToStep}
            />
          ))}
        <NewLookStepWrapper>{CurrentStep}</NewLookStepWrapper>
      </NewLookFormPanel>
    );
  }

  return (
    <Wrapper>
      <PageCard>
        {getStepsHeader()}
        <StepPageWrapper>{CurrentStep}</StepPageWrapper>
      </PageCard>
    </Wrapper>
  );
};

export default TwoFAWizard;

const StepPageWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-top: 40px;
  flex-direction: column;
  position: relative;
  height: 300px;
`;
const Wrapper = styled.div`
  padding: 16px;
  display: flex;
  justify-content: center;
`;
const PageCard = styled(Card)`
  width: 700px;
  cursor: unset;

  @media (max-width: ${COLLAPSED_DESKTOP_MENU}) {
    max-width: calc(100vw - 20px);
  }
`;

const NewLookStepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding-top: 24px;
  text-align: center;
`;
const NewLookStepCaption = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
